import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Buildings, CurrencyDollar, ArrowRight } from '@phosphor-icons/react';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await api.get('/companies');
      setCompanies(data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchCompany = async (companyId) => {
    try {
      await api.post(`/companies/${companyId}/switch`);
      toast.success('Empresa activa cambiada');
    } catch (error) {
      toast.error('Error al cambiar empresa');
    }
  };

  const CreateCompanyModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      legal_name: '',
      tax_id: '',
      country: '',
      currency: 'USD',
      address: '',
      phone: '',
      email: '',
      taxes: []
    });

    const [taxForm, setTaxForm] = useState({ tax_name: '', tax_rate: 0, is_default: false });

    const addTax = () => {
      if (taxForm.tax_name && taxForm.tax_rate > 0) {
        setFormData({
          ...formData,
          taxes: [...formData.taxes, taxForm]
        });
        setTaxForm({ tax_name: '', tax_rate: 0, is_default: false });
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await api.post('/companies', formData);
        toast.success('Empresa creada exitosamente');
        setShowCreateModal(false);
        fetchCompanies();
      } catch (error) {
        toast.error('Error al crear empresa');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-neutral-900 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold text-white mb-4">Nueva Empresa</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nombre Comercial *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Razón Social *</label>
                <input
                  type="text"
                  required
                  value={formData.legal_name}
                  onChange={(e) => setFormData({...formData, legal_name: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">RUC/NIT/RFC *</label>
                <input
                  type="text"
                  required
                  value={formData.tax_id}
                  onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">País *</label>
                <input
                  type="text"
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Moneda *</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="MXN">MXN</option>
                  <option value="COP">COP</option>
                  <option value="PAB">PAB</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>

            {/* Taxes Configuration */}
            <div className="border-t border-neutral-800 pt-4">
              <h4 className="text-white font-semibold mb-3">Configuración de Impuestos</h4>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Nombre (ej: ITBMS)"
                  value={taxForm.tax_name}
                  onChange={(e) => setTaxForm({...taxForm, tax_name: e.target.value})}
                  className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Tasa %"
                  value={taxForm.tax_rate}
                  onChange={(e) => setTaxForm({...taxForm, tax_rate: parseFloat(e.target.value)})}
                  className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <button
                  type="button"
                  onClick={addTax}
                  className="bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Añadir
                </button>
              </div>
              {formData.taxes.length > 0 && (
                <div className="space-y-1">
                  {formData.taxes.map((tax, idx) => (
                    <div key={idx} className="bg-neutral-800/50 rounded px-3 py-2 text-sm text-gray-300">
                      {tax.tax_name}: {tax.tax_rate}%
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Crear Empresa
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Multi-Empresa">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Multi-Empresa</h1>
            <p className="text-gray-400 mt-1">Gestión de múltiples empresas y configuración fiscal</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nueva Empresa
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-8 text-gray-400">Cargando...</div>
          ) : companies.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-400">
              No hay empresas configuradas. Crea la primera.
            </div>
          ) : (
            companies.map((company) => (
              <div
                key={company._id}
                className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 hover:border-emerald-600/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <Buildings size={32} className="text-emerald-400" />
                  {company.is_active && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
                      Activa
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{company.name}</h3>
                <p className="text-sm text-gray-400 mb-3">{company.legal_name}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">RUC/NIT:</span>
                    <span className="text-white">{company.tax_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">País:</span>
                    <span className="text-white">{company.country}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Moneda:</span>
                    <span className="text-white flex items-center gap-1">
                      <CurrencyDollar size={16} />
                      {company.currency}
                    </span>
                  </div>
                </div>

                {company.taxes && company.taxes.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-800">
                    <p className="text-xs text-gray-400 mb-2">Impuestos configurados:</p>
                    <div className="space-y-1">
                      {company.taxes.map((tax, idx) => (
                        <div key={idx} className="text-xs text-gray-300">
                          {tax.tax_name}: {tax.tax_rate}%
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleSwitchCompany(company._id)}
                  className="mt-4 w-full bg-neutral-800 hover:bg-emerald-600 text-white py-2 rounded-lg transition-colors text-sm"
                >
                  Activar Empresa
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {showCreateModal && <CreateCompanyModal />}
    </Layout>
  );
}
